import { useEffect, useRef, useState } from "react";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Loader2, Smartphone, CreditCard, Ticket, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

function SeatMap({ eventId, cat, selected, onToggle, registerRefetch }) {
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    return endpoints.eventSeatmap(eventId, cat.id)
      .then((r) => setMap(r.data))
      .catch((e) => toast.error(apiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); if (registerRefetch) registerRefetch(cat.id, load); /* eslint-disable-next-line */ }, [cat.id]);

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E11D2A]" /></div>;
  if (!map) return null;
  const chosen = new Set(selected.map((s) => s.seat));

  return (
    <div className="pt-3" data-testid={`seatmap-${cat.id}`}>
      <div className="mx-auto mb-3 max-w-xs text-center">
        <div className="h-1.5 rounded-full bg-gradient-to-r from-transparent via-[#E11D2A] to-transparent" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-1">Stage / Screen</div>
      </div>
      <div className="overflow-x-auto pb-2">
        <div className="inline-grid gap-1.5 mx-auto" style={{ gridTemplateColumns: `repeat(${map.cols}, minmax(2.1rem, 1fr))` }}>
          {map.seats.map((s) => {
            const isChosen = chosen.has(s.seat);
            const cls = s.taken
              ? "bg-[#1b2130] text-slate-600 cursor-not-allowed border-transparent"
              : isChosen
                ? "bg-emerald-500 text-white border-emerald-400 shadow-[0_0_0_2px_rgba(16,185,129,0.35)]"
                : "bg-[#0A0C10] text-slate-300 border-[#273042] hover:border-[#E11D2A]/60 hover:text-white";
            return (
              <button key={s.seat} disabled={s.taken} onClick={() => onToggle(cat, s)}
                data-testid={`seat-${cat.id}-${s.label}`}
                data-taken={s.taken ? "1" : "0"} data-selected={isChosen ? "1" : "0"}
                className={`h-9 w-9 rounded-md border text-[10px] font-bold flex items-center justify-center transition-colors ${cls}`}>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 justify-center mt-3 text-[11px] text-slate-400">
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-[#0A0C10] border border-[#273042]" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-emerald-500" /> Selected</span>
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-[#1b2130]" /> Taken</span>
      </div>
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const { isAuthed, user } = useAuth();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});   // categoryId -> [{seat, label}]
  const [openCat, setOpenCat] = useState(null);
  const [method, setMethod] = useState("momo");
  const ticketMethods = usePaymentMethods("tickets");
  useEffect(() => {
    const avail = ["momo", "stripe"].filter((m) => !ticketMethods || ticketMethods.includes(m));
    if (avail.length && !avail.includes(method)) setMethod(avail[0]);
  }, [ticketMethods, method]);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const refetchers = useRef({});

  useEffect(() => { endpoints.event(id).then((r) => setEv(r.data)).catch((e) => toast.error(apiError(e?.response?.data?.detail))).finally(() => setLoading(false)); }, [id]);
  useEffect(() => { if (user?.phone) setPhone(user.phone); if (user?.email) setEmail(user.email); }, [user]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  if (!ev) return <div className="text-center py-32 text-slate-500">Event not found. <Link to="/events" className="text-[#E11D2A]">All events</Link></div>;

  const priceOf = (cid) => (ev.categories.find((c) => c.id === cid)?.price || 0);
  const registerRefetch = (cid, fn) => { refetchers.current[cid] = fn; };
  const toggleSeat = (cat, s) => {
    setSelected((prev) => {
      const cur = prev[cat.id] || [];
      const exists = cur.some((x) => x.seat === s.seat);
      const next = exists ? cur.filter((x) => x.seat !== s.seat) : [...cur, { seat: s.seat, label: s.label }];
      return { ...prev, [cat.id]: next };
    });
  };

  const allSeats = Object.entries(selected).flatMap(([cid, arr]) => arr.map((s) => ({ ...s, cid })));
  const count = allSeats.length;
  const total = allSeats.reduce((sum, s) => sum + priceOf(s.cid), 0);
  const onsale = ev.status === "onsale";

  const pollMomo = async (ref) => {
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const { data } = await endpoints.ticketsMomoStatus(ref);
        if (data.paid) { toast.success("Payment confirmed — tickets sent by WhatsApp & email! 🎟️"); window.location.href = "/my-tickets"; return; }
        if (data.status === "failed") { toast.error("Payment failed or declined. No ticket was issued — you can try again."); return; }
      } catch { /* keep polling */ }
    }
    toast.message("Still pending — your tickets arrive automatically once payment confirms. Check ‘My Tickets’ shortly.");
  };

  const buildItems = () => Object.entries(selected)
    .filter(([, arr]) => arr.length > 0)
    .map(([cid, arr]) => ({ categoryId: cid, seats: arr.map((s) => s.seat) }));

  const refetchAll = () => Object.values(refetchers.current).forEach((fn) => fn && fn());

  const buy = async () => {
    if (!isAuthed) { window.dispatchEvent(new Event("open-auth")); return; }
    if (count < 1) { toast.error("Tap the seats you want on the map"); return; }
    if (phone.replace(/[^\d]/g, "").length < 9) { toast.error("Enter your WhatsApp number so we can send your ticket"); return; }
    const items = buildItems();
    setBusy(true);
    try {
      if (method === "stripe") {
        const origin = window.location.origin;
        const { data } = await endpoints.ticketsPurchase({ eventId: id, items, method: "stripe", currency: "rwf", phone, email,
          success_url: `${origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`, cancel_url: `${origin}/events/${id}` });
        window.location.href = data.url;
      } else {
        const { data } = await endpoints.ticketsPurchase({ eventId: id, items, method: "momo", phone, email });
        toast.success("Approve the payment on your phone (MoMo PIN)…");
        await pollMomo(data.reference);
      }
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
      refetchAll(); // a seat may have just been taken — refresh availability
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-8">
      <Link to="/events" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-5"><ArrowLeft className="w-4 h-4" /> All events</Link>
      <div className="rounded-2xl overflow-hidden border border-[#273042] bg-[#121620] mb-6">
        <div className="aspect-[21/9] relative bg-[#0A0C10]">
          <img src={ev.coverUrl || "/bb-logo.png"} alt={ev.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
          <div className="absolute bottom-0 left-0 p-5"><h1 className="text-2xl sm:text-3xl font-extrabold text-white">{ev.title}</h1></div>
        </div>
        <div className="p-5">
          {ev.description && <p className="text-slate-300 text-sm mb-3">{ev.description}</p>}
          <div className="flex flex-wrap gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmt(ev.startAt)}</span>
            {ev.venue && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {ev.venue}{ev.location ? `, ${ev.location}` : ""}</span>}
          </div>
        </div>
      </div>

      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Pick your seats</div>
      <div className="mb-4 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400" data-testid="ticket-journey">
        {["Pick seats", "Pay", "Confirmed", "WhatsApp + Email", "My Tickets", "QR entry"].map((step, i, arr) => (
          <span key={step} className="flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-full bg-[#121620] border border-[#273042] text-slate-300">{i + 1}. {step}</span>
            {i < arr.length - 1 && <span className="text-slate-600">→</span>}
          </span>
        ))}
      </div>

      <div className="space-y-3" data-testid="ticket-categories">
        {ev.categories.map((c) => {
          const chosen = selected[c.id] || [];
          const open = openCat === c.id;
          return (
            <div key={c.id} data-testid={`ticket-cat-${c.id}`} className="rounded-xl border border-[#273042] bg-[#121620] overflow-hidden">
              <button onClick={() => setOpenCat(open ? null : c.id)} data-testid={`cat-toggle-${c.id}`} className="w-full flex items-center gap-3 p-4 text-left">
                {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold">{c.name}</div>
                  <div className="text-xs text-slate-400">{c.price.toLocaleString()} {c.currency}{c.remaining != null ? ` · ${c.remaining} left` : ""}</div>
                </div>
                {chosen.length > 0 && (
                  <span className="text-xs font-bold text-emerald-400 whitespace-nowrap" data-testid={`cat-chosen-${c.id}`}>
                    {chosen.length} seat{chosen.length > 1 ? "s" : ""}: {chosen.map((s) => s.label).join(", ")}
                  </span>
                )}
              </button>
              {open && (
                <div className="px-4 pb-4 border-t border-[#273042]">
                  <SeatMap eventId={id} cat={c} selected={chosen} onToggle={toggleSeat} registerRefetch={registerRefetch} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {onsale ? (
        <div className="mt-6 rounded-2xl border border-[#273042] bg-[#121620] p-5">
          {count > 0 && (
            <div className="mb-3 text-xs text-slate-300" data-testid="selected-summary">
              Selected: {allSeats.map((s) => s.label).join(", ")}
            </div>
          )}
          <div className="flex items-center justify-between mb-4"><span className="text-slate-300 font-semibold">{count} seat(s)</span><span className="text-2xl font-extrabold text-[#F5B301]" data-testid="tickets-total">{total.toLocaleString()} RWF</span></div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[["momo", "MTN MoMo", Smartphone], ["stripe", "Card", CreditCard]].filter(([m]) => !ticketMethods || ticketMethods.includes(m)).map(([m, l, I]) => (
              <button key={m} onClick={() => setMethod(m)} data-testid={`ticket-method-${m}`} className={`flex items-center gap-2 p-3 rounded-xl border text-sm font-semibold ${method === m ? "border-[#E11D2A] bg-[#E11D2A]/10 text-white" : "border-[#273042] bg-[#0A0C10] text-slate-300"}`}><I className="w-4 h-4" /> {l}</button>
            ))}
          </div>
          {method === "momo" && <div className="text-[11px] text-slate-400 mb-1.5">Enter the MoMo number to pay with — we also send your ticket to this WhatsApp number.</div>}
          <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="ticket-momo-phone" placeholder="WhatsApp number (required) e.g. +250788123456" className="w-full h-12 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white mb-3 outline-none focus:border-[#E11D2A]/60" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ticket-email" placeholder="Email for your ticket & receipt (optional)" className="w-full h-12 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white mb-3 outline-none focus:border-[#E11D2A]/60" />
          <button onClick={buy} disabled={busy || count < 1} data-testid="buy-tickets-btn" className="w-full h-12 rounded-xl bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</> : <><Ticket className="w-5 h-5" /> Buy {count > 0 ? `${count} ` : ""}seat(s)</>}
          </button>
          <p className="mt-3 text-[11px] text-slate-500 text-center">Your seats are held while you pay. Tickets are issued only after payment confirms, then sent via WhatsApp & email with a QR code.</p>
        </div>
      ) : <div className="mt-6 text-center text-slate-400 text-sm py-6 rounded-2xl border border-[#273042] bg-[#121620]">Ticket sales are closed for this event.</div>}
    </div>
  );
}
